import React, { useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../i18n/LanguageContext.js';
import reg_professional from '../img/register_professional_hero.jpeg';
import {swissTownData} from '../data/swissTownData.js';

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};


const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

const getNestedValue = (state, path) => {
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), state);
};

const updateNestedValue = (state, path, value) => {
  const [first, ...rest] = path.split('.');
  if (rest.length === 0) {
    return { ...state, [first]: value };
  }
  return {
    ...state,
    [first]: updateNestedValue({ ...state[first] }, rest.join('.'), value),
  };
};

export default function RegisterProfessional() {
  const { strings } = useLanguage();
  const form = strings.pages.registration.form || {};
  const errors = strings.pages.registration.errors || {};

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    telephone: '',
    website: '',
    employmentStatus: '',
    company: '',
    legalBusinessForm: '',
    professionServices: '',
    experienceInSpecialization: '',
    placeOfOperation: { canton: '', town: '' },
    languagesSpoken: {
      russian: false,
      english: false,
      french: false,
      german: false,
      other: false,
    },
    motivationToJoin: {
      professionalContacts: false,
      projectBusinessPartner: false,
      careerOpportunities: false,
      clients: false,
      mentor: false,
      becomeMentor: false,
      becomeSpeaker: false,
      communityExpertise: false,
      likeMindedPeople: false,
      other: false,
    },
    associationDataConsent: false,
  });

  const [fieldValidation, setFieldValidation] = useState({
    email: null,
    firstName: null,
    lastName: null,
    telephone: null,
    website: null,
    employmentStatus: null,
    company: null,
    legalBusinessForm: null,
    professionServices: null,
    experienceInSpecialization: null,
    placeOfOperation: null,
    languagesSpoken: null,
    motivationToJoin: null,
    associationDataConsent: null,
  });

  const [visibleFields, setVisibleFields] = useState(['email']);
  const [completedFields, setCompletedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const fieldSequence = [
    'email',
    'firstName',
    'lastName',
    'telephone',
    'website',
    'employmentStatus',
    'company',
    'legalBusinessForm',
    'professionServices',
    'experienceInSpecialization',
    'placeOfOperation',
    'languagesSpoken',
    'motivationToJoin',
    'associationDataConsent',
  ];

  const validateField = useCallback((fieldName, value) => {
    switch (fieldName) {
      case 'email':
        return value.trim() === '' ? false : validateEmail(value);
      case 'firstName':
      case 'lastName':
      case 'employmentStatus':
      case 'company':
      case 'legalBusinessForm':
        return value.trim().length > 0;
      case 'telephone':
        return value.trim() === '' ? false : validatePhone(value);
      case 'website':
      case 'professionServices':
        return value.trim().length > 0 ? true : null;
      case 'experienceInSpecialization':
        return value.trim().length > 0 ? true : null;
      case 'placeOfOperation':
        return value.canton && value.town ? true : null;
      case 'languagesSpoken':
        return Object.values(value).some(Boolean) ? true : null;
      case 'motivationToJoin':
        return Object.values(value).some(Boolean) ? true : null;
      case 'associationDataConsent':
        return value === true;
      default:
        return null;
    }
  }, []);

  const validateFieldImmediately = useCallback(
    (fieldName, value) => {
      const validation = validateField(fieldName, value);
      if (validation === null) {
        setFieldValidation((prev) => ({ ...prev, [fieldName]: null }));
        setCompletedFields((prev) => ({ ...prev, [fieldName]: false }));
        return null;
      }

      setFieldValidation((prev) => ({
        ...prev,
        [fieldName]: validation ? 'valid' : 'invalid',
      }));

      if (validation) {
        setCompletedFields((prev) => ({ ...prev, [fieldName]: true }));
        revealNextField(fieldName);
      } else {
        setCompletedFields((prev) => ({ ...prev, [fieldName]: false }));
      }
      return validation;
    },
    [validateField]
  );

  const debouncedValidate = useMemo(
    () => debounce((fieldName, value) => validateFieldImmediately(fieldName, value), 300),
    [validateFieldImmediately]
  );

  const revealNextField = (currentField) => {
    const currentIndex = fieldSequence.indexOf(currentField);
    if (currentIndex === -1 || currentIndex === fieldSequence.length - 1) {
      return;
    }

    const nextField = fieldSequence[currentIndex + 1];
    setVisibleFields((prev) => (prev.includes(nextField) ? prev : [...prev, nextField]));
  };

  const handleSkipField = (fieldName) => {
    setFieldValidation((prev) => ({ ...prev, [fieldName]: 'valid' }));
    setCompletedFields((prev) => ({ ...prev, [fieldName]: true }));
    revealNextField(fieldName);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const isNested = name.includes('.');

    if (type === 'checkbox' && isNested) {
      setFormData((prev) => {
        const updated = updateNestedValue(prev, name, checked);
        if (name.startsWith('languagesSpoken') || name.startsWith('motivationToJoin')) {
          debouncedValidate(name.split('.')[0], getNestedValue(updated, name.split('.')[0]));
        }
        return updated;
      });
      return;
    }

    if (type === 'checkbox') {
      const valueToSet = checked;
      setFormData((prev) => {
        const updated = { ...prev, [name]: valueToSet };
        if (name === 'consentAdvertisingAndEvents' || name === 'consentOffersAndServices') {
          validateFieldImmediately('marketingConsents', {
            consentAdvertisingAndEvents: updated.consentAdvertisingAndEvents,
            consentOffersAndServices: updated.consentOffersAndServices,
          });
        }
        if (name === 'associationDataConsent') {
          validateFieldImmediately('associationDataConsent', valueToSet);
        }
        return updated;
      });
      return;
    }

    if (isNested) {
      setFormData((prev) => {
        const updated = updateNestedValue(prev, name, value);
        if (name.startsWith('placeOfOperation')) {
          const placeValue = getNestedValue(updated, 'placeOfOperation');
          validateFieldImmediately('placeOfOperation', placeValue);
        }
        return updated;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    debouncedValidate(name, value);
  };

  const handleFieldBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const isNested = name.includes('.');

    if (type === 'checkbox' && isNested) {
      return;
    }

    if (type === 'checkbox') {
      return;
    }

    if (isNested) {
      const fieldName = name.split('.')[0];
      const nestedValue = getNestedValue(formData, fieldName);
      validateFieldImmediately(fieldName, nestedValue);
      return;
    }

    validateFieldImmediately(name, value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const requiredValidation = [
      ['email', formData.email],
      ['firstName', formData.firstName],
      ['lastName', formData.lastName],
      ['telephone', formData.telephone],
      ['employmentStatus', formData.employmentStatus],
      ['company', formData.company],
      ['legalBusinessForm', formData.legalBusinessForm],
      ['associationDataConsent', formData.associationDataConsent],
    ];

    const allValid = requiredValidation.every(([field, value]) => validateFieldImmediately(field, value));
    if (!allValid) {
      return;
    }

    const registrationUrl = process.env.REACT_APP_REGISTRATION_URL || '';
    if (!registrationUrl) {
      console.error('REACT_APP_REGISTRATION_URL is not set');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    fetch(registrationUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
      credentials: 'include',
    })
      .then(async (res) => {
        setIsSubmitting(false);
        if (!res.ok) {
          const text = await res.text();
          console.error('Registration failed', res.status, text);
          setSubmitStatus('error');
          return;
        }
        setSubmitStatus('success');
      })
      .catch((err) => {
        console.error('Registration error', err);
        setIsSubmitting(false);
        setSubmitStatus('error');
      });
  };

  const getFieldError = (fieldName) => {
    if (fieldValidation[fieldName] === 'invalid') {
      switch (fieldName) {
        case 'email':
          return errors.invalidEmail || 'Please enter a valid email address.';
        case 'telephone':
          return errors.invalidTelephone || 'Please enter a valid telephone number.';
        case 'firstName':
          return errors.firstNameRequired || 'First name is required.';
        case 'lastName':
          return errors.lastNameRequired || 'Last name is required.';
        case 'employmentStatus':
          return errors.employmentStatusRequired || 'Please choose your employment status.';
        case 'company':
          return errors.companyRequired || 'Company name is required.';
        case 'legalBusinessForm':
          return errors.legalBusinessFormRequired || 'Please select a legal business form.';
        case 'placeOfOperation':
          return errors.placeOfOperationRequired || 'Please choose a canton and town or skip this field.';
        case 'associationDataConsent':
          return errors.associationDataConsentRequired || 'Please agree to the data processing consent.';
        default:
          return '';
      }
    }
    return '';
  };

  const getFieldStatus = (fieldName) => {
    if (fieldValidation[fieldName] === 'valid') return 'valid';
    if (fieldValidation[fieldName] === 'invalid') return 'invalid';
    return 'neutral';
  };

  const optionalFields = [
    'website',
    'professionServices',
    'experienceInSpecialization',
    'placeOfOperation',
    'languagesSpoken'
  ];

  const renderLabelRow = (fieldName, required) => {
    const label = form[fieldName] || fieldName;
    const optional = optionalFields.includes(fieldName);
    return (
      <div className="form-label-row">
        <label htmlFor={fieldName} className="form-label">
          {label}
          {optional && <span className="form-optional">{form.optionalLabel || ' (Optional)'}</span>}
        </label>
        {optional && (
          <button
            type="button"
            onClick={() => handleSkipField(fieldName)}
            className="form-skip-btn"
            aria-label={`${form.skipLabel || 'Skip'} ${label}`}
          >
            {form.skipLabel || 'Skip'}
          </button>
        )}
      </div>
    );
  };

  const renderTextField = (fieldName, type = 'text') => {
    const status = getFieldStatus(fieldName);
    const placeholder = form[`${fieldName}Placeholder`] || '';
    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key={fieldName}>
        {renderLabelRow(fieldName, fieldName !== 'associationDataConsent')}
        <input
          id={fieldName}
          name={fieldName}
          type={type}
          value={formData[fieldName]}
          placeholder={placeholder}
          className={`form-input form-input-${status}`}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          aria-label={form[fieldName] || fieldName}
        />
        {getFieldError(fieldName) && <p className="form-error-message">{getFieldError(fieldName)}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };

  const renderSelectField = (fieldName, options) => {
    const status = getFieldStatus(fieldName);
    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key={fieldName}>
        {renderLabelRow(fieldName, false)}
        <select
          id={fieldName}
          name={fieldName}
          value={formData[fieldName]}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`form-input form-input-${status}`}
          aria-label={form[fieldName] || fieldName}
        >
          <option value="">{form.selectPlaceholder || 'Select...'}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {getFieldError(fieldName) && <p className="form-error-message">{getFieldError(fieldName)}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };

  const renderRadioGroup = (fieldName, options) => {
    const status = getFieldStatus(fieldName);
    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key={fieldName}>
        <div className="form-label-row">
          <span className="form-label">{form[fieldName] || fieldName}</span>
        </div>
        <div className="radio-group">
          {options.map((option) => (
            <label key={option} className="radio-option">
              <input
                type="radio"
                name={fieldName}
                value={option}
                checked={formData[fieldName] === option}
                onChange={handleInputChange}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
        {getFieldError(fieldName) && <p className="form-error-message">{getFieldError(fieldName)}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };

  const renderTextAreaField = (fieldName) => {
    const status = getFieldStatus(fieldName);
    const placeholder = form[`${fieldName}Placeholder`] || '';
    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key={fieldName}>
        {renderLabelRow(fieldName, true)}
        <textarea
          id={fieldName}
          name={fieldName}
          value={formData[fieldName]}
          placeholder={placeholder}
          className={`form-input form-input-${status}`}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          rows={6}
        />
        {getFieldError(fieldName) && <p className="form-error-message">{getFieldError(fieldName)}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };

  const renderPlaceOfOperation = () => {
    const status = getFieldStatus('placeOfOperation');
    const cantonOptions = swissTownData.map((item) => item.canton);
    const selectedCanton = formData.placeOfOperation.canton;
    const cantonData = swissTownData.find((item) => item.canton === selectedCanton);
    const townOptions = cantonData ? cantonData.towns : [];

    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key="placeOfOperation">
        {renderLabelRow('placeOfOperation', true)}
        <select
          id="placeOfOperation.canton"
          name="placeOfOperation.canton"
          value={formData.placeOfOperation.canton}
          onChange={handleInputChange}
          onBlur={handleFieldBlur}
          className={`form-input form-input-${status}`}
        >
          <option value="">{form.cantonPlaceholder || 'Choose a canton'}</option>
          {cantonOptions.map((canton) => (
            <option key={canton} value={canton}>
              {canton}
            </option>
          ))}
        </select>
        {selectedCanton && (
          <select
            id="placeOfOperation.town"
            name="placeOfOperation.town"
            value={formData.placeOfOperation.town}
            onChange={handleInputChange}
            onBlur={handleFieldBlur}
            className={`form-input form-input-${status}`}
          >
            <option value="">{form.townPlaceholder || 'Choose a town'}</option>
            {townOptions.map((town) => (
              <option key={town} value={town}>
                {town}
              </option>
            ))}
          </select>
        )}
        {getFieldError('placeOfOperation') && <p className="form-error-message">{getFieldError('placeOfOperation')}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };

  const renderCheckboxGroup = (fieldName, options, label) => {
    const status = getFieldStatus(fieldName);
    const groupState = getNestedValue(formData, fieldName);
    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key={fieldName}>
        <div className="form-label-row">
          <span className="form-label">{label}</span>
          <button
            type="button"
            onClick={() => handleSkipField(fieldName)}
            className="form-skip-btn"
            aria-label={`${form.skipLabel || 'Skip'} ${label}`}
          >
            {form.skipLabel || 'Skip'}
          </button>
        </div>
        <div className="checkbox-group">
          {options.map(({ value, text }) => (
            <label key={value} className="checkbox-option">
              <input
                type="checkbox"
                name={`${fieldName}.${value}`}
                checked={groupState[value]}
                onChange={handleInputChange}
              />
              <span>{text}</span>
            </label>
          ))}
        </div>
        {getFieldError(fieldName) && <p className="form-error-message">{getFieldError(fieldName)}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };


  const renderAgreementConsent = () => {
    const status = getFieldStatus('associationDataConsent');
    return (
      <div className={`form-field-wrapper field-visible ${status === 'invalid' ? 'field-error' : ''}`} key="associationDataConsent">
        <div className="form-label-row">
          <label htmlFor="associationDataConsent" className="form-label">
            {form.associationDataConsentLabel || 'Agreement / Согласие'}
          </label>
        </div>
        <div className="checkbox-group">
          <label className="checkbox-option">
            <input
              type="checkbox"
              id="associationDataConsent"
              name="associationDataConsent"
              checked={formData.associationDataConsent}
              onChange={handleInputChange}
            />
            <span className="association-consent-text">
              {form.associationDataConsentText ||
                'I consent to the processing of my personal data by the Association of Russian-speaking Professionals in Switzerland. I understand that my data is used exclusively for the formation of a database of specialists and the development of a professional community; my contact information may be available to other members of the Association for networking and collaboration purposes; my data is not shared with third parties outside the Association without my additional consent; I can request a change or deletion of my data at any time.'}
            </span>
          </label>
        </div>
        {getFieldError('associationDataConsent') && <p className="form-error-message">{getFieldError('associationDataConsent')}</p>}
        {status === 'valid' && <p className="form-success-message">✓</p>}
      </div>
    );
  };

  const employmentOptions = form.employmentStatusOptions || [
    'Наёмный сотрудник / Employed',
    'Самозанятый / Предприниматель / Self-employed / Entrepreneur',
    'В поиске работы / Looking for a job',
    'Меняю сферу / Переквалификация / Career changer',
    'Студент / Student',
  ];

  const legalBusinessFormOptions = form.legalBusinessFormOptions || [
    'Sole Proprietorship (e.g., Raison Individuelle / Einzelfirma)',
    'Partnership (e.g., Snc / Kollektivgesellschaft)',
    'Limited Liability Company (e.g., Sàrl / GmbH)',
    'Corporation (e.g., SA / AG)',
    'N/A',
  ];

  const experienceOptions = form.experienceOptions || [
    'Less Than 6 months',
    '6 months to 2 years',
    '2 years to 5 years',
    'More than 5 years',
  ];

  const languageOptions = form.languagesSpokenOptions || [
    { value: 'russian', text: 'Русский / Russian' },
    { value: 'english', text: 'Английский / English' },
    { value: 'french', text: 'Французский / French' },
    { value: 'german', text: 'Немецкий / German' },
    { value: 'other', text: 'Другой / Other' },
  ];

  const motivationOptions = form.motivationOptions || [
    { value: 'professionalContacts', text: 'Профессиональные контакты в своей сфере / Professional contacts in your field' },
    { value: 'projectBusinessPartner', text: 'Партнера для проекта или бизнеса / Partner for a project or business' },
    { value: 'careerOpportunities', text: 'Карьерные возможности / Career opportunities' },
    { value: 'clients', text: 'Клиентов / Clients' },
    { value: 'mentor', text: 'Ментора / Mentor' },
    { value: 'becomeMentor', text: 'Стать ментором / Become a mentor' },
    { value: 'becomeSpeaker', text: 'Стать спикером / Become a speaker' },
    { value: 'communityExpertise', text: 'Доступ к экспертизе сообщества / Access to community expertise' },
    { value: 'likeMindedPeople', text: 'Просто своих людей рядом / Just like-minded people around' },
    { value: 'other', text: 'Другое / Other' },
  ];

  const allRequiredComplete =
    fieldValidation.email === 'valid' &&
    fieldValidation.firstName === 'valid' &&
    fieldValidation.lastName === 'valid' &&
    fieldValidation.telephone === 'valid' &&
    fieldValidation.employmentStatus === 'valid' &&
    fieldValidation.company === 'valid' &&
    fieldValidation.legalBusinessForm === 'valid' &&
    fieldValidation.associationDataConsent === 'valid';

  return (
    <div>
  <section className="section">
  <div className="section-tag">{strings.pages.registration.eyebrow}</div>
  <div className="event_hero" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
    <div className="event_hero__content" style={{ maxWidth: '50%', zIndex: 2 }}>
      <h1 className="event_hero__title">
        Анкета участников базы данных <em className="event_hero__title-accent">Ассоциации русскоязычных профессионалов Швейцарии</em>
      </h1>

      <p className="event_hero__subtitle">
        На основе этой анкеты формируется ваш профиль в каталоге участников.
        <br /> <br />
        По указанным данным другие профессионалы смогут найти вас, оценить вашу экспертизу и связаться для сотрудничества, 
        проектов и мероприятий. Информация используется для нетворкинга и профессионального взаимодействия
      </p>
    </div>

    <div className="event_hero__media">
      <div className="event_hero__overlay" />
      <img 
        src={reg_professional}
        alt="RPSwiss Registration" 
        className="event_hero__image" 
      />
    </div>
  </div>
</section>

      <section className="registration-form-section">
        <div className="form-container">
          <h2 className="form-title">{form.formTitle || 'Complete Your Profile'}</h2>
          <p className="form-subtitle">{form.formSubtitle || 'Tell us a bit about yourself. Fields marked with * are required.'}</p>

          <form className="adaptive-form" onSubmit={handleSubmit}>
            {visibleFields.includes('email') && renderTextField('email', 'email')}
            {visibleFields.includes('firstName') && renderTextField('firstName')}
            {visibleFields.includes('lastName') && renderTextField('lastName')}
            {visibleFields.includes('telephone') && renderTextField('telephone', 'tel')}
            {visibleFields.includes('website') && renderTextField('website')}
            {visibleFields.includes('employmentStatus') && renderRadioGroup('employmentStatus', employmentOptions)}
            {visibleFields.includes('company') && renderTextField('company')}
            {visibleFields.includes('legalBusinessForm') && renderSelectField('legalBusinessForm', legalBusinessFormOptions)}
            {visibleFields.includes('professionServices') && renderTextAreaField('professionServices')}
            {visibleFields.includes('experienceInSpecialization') && renderSelectField('experienceInSpecialization', experienceOptions)}
            {visibleFields.includes('placeOfOperation') && renderPlaceOfOperation()}
            {visibleFields.includes('languagesSpoken') && renderCheckboxGroup('languagesSpoken', languageOptions, form.languagesSpokenTitle || 'Languages Spoken / Языки, которыми вы владеете')}
            {visibleFields.includes('motivationToJoin') && renderCheckboxGroup('motivationToJoin', motivationOptions, form.motivationToJoinTitle || 'What is your motivation to join RPSwiss / Что вы хотите найти в RPSwiss')}
            {visibleFields.includes('associationDataConsent') && renderAgreementConsent()}

            {allRequiredComplete && (
              <div className="form-field-wrapper submit-wrapper field-visible">
                <button
                  type="submit"
                  className="form-submit-btn"
                  aria-label={form.submitButtonLabel || 'Submit registration'}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? form.submittingText || 'Submitting...' : form.submitButton || 'Complete Registration'}
                </button>
              </div>
            )}

            {submitStatus === 'success' && <p className="form-success-message">{form.submitSuccessMessage || 'Registration submitted successfully.'}</p>}
            {submitStatus === 'error' && <p className="form-error-message">{form.submitErrorMessage || 'There was an error submitting your registration. Please try again.'}</p>}
          </form>

          <div className="form-progress">
            <p className="progress-text">
              {form.progressLabel || 'Progress'}: <span className="progress-count">{Object.values(completedFields).filter(Boolean).length} {form.progressOf || 'of'} {fieldSequence.length}</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
